import lodashSnakeCase from 'lodash-es/snakeCase';

import { eventTypeStandard, getPageType, push } from './shared';

import { type MeUserEntity, type UserEntity } from '~/apiClient';

export type UseTrackingSignupReturn = {
    trackSignupFieldClick: (fieldName: string) => void;
    trackSignupFormError: (error: string) => void;
    trackSignupCreateClick: VoidFunction;
    trackSignupStandardFormSubmitted: (user: UserEntity) => void;
    trackSignupCheckEmailView: (email?: string) => void;
    trackSignupSuccess: (user: UserEntity | MeUserEntity) => void;
};

export const useTrackingSignup = (): UseTrackingSignupReturn => {
    const { locale } = useTypedI18n();

    return {
        trackSignupFieldClick(fieldName: string): void {
            push({
                event: 'click',
                click_element: 'field',
                click_text: fieldName,
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },
        trackSignupFormError(error: string): void {
            push({
                event: 'form_error',
                error_type: error,
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },
        trackSignupCreateClick(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'create_account',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },
        trackSignupStandardFormSubmitted(user: UserEntity): void {
            push({
                event: 'signup_form_success',
                user_id: user.id,
                user_email: user.email,
                page_type: getPageType(),
                click_element: 'standard_form',
                ...eventTypeStandard,
            });
        },
        trackSignupCheckEmailView(email?: string): void {
            push({
                event: 'page_view',
                user_email: email ?? 'unknown',
                click_text: 'check_email',
                page_type: 'signup_check_email',
                http_status_code: '200',
                lang: locale.value,
                ...eventTypeStandard,
            });
        },
        trackSignupSuccess(user): void {
            push({
                event: 'sign_up',
                user_id: user.id,
                user_email: user.email,
                user_info: {
                    first_name: lodashSnakeCase(user.firstname ?? 'unknown'),
                    last_name: lodashSnakeCase(user.lastname ?? 'unknown'),
                    user_phone: lodashSnakeCase(user.phone ?? 'unknown'),
                },
                company_info: {
                    company_name: lodashSnakeCase(user.company?.businessName),
                    company_id: user.company?.id,
                    vat: lodashSnakeCase(user.company?.tin ?? 'unknown'),
                    address: lodashSnakeCase(user.company?.address),
                    city: lodashSnakeCase(user.company?.city),
                    region: lodashSnakeCase(user.company?.province ?? 'unknown'),
                    country: lodashSnakeCase(user.company?.countryCode),
                    zip_code: lodashSnakeCase(user.company?.postCode),
                    operator_type: lodashSnakeCase(user.company?.operatorType),
                },
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },
    };
};
